import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  ExamResultState,
  ExamSectionSummary,
  InstructionSection,
  ExamSubmissionSummary,
  PaletteState,
  QuestionPaperItem,
  QuestionPaperPopupMode,
  QuestionPart,
  ResumeData,
  SubmitExamPayload,
  SubmitExamSectionPayload,
} from '../../model/test-series.model';
import { DatePipe } from '@angular/common';
import { ImageEnum } from '../../../../shared/utils/image.enum';
import { TitleCasePipe } from '../../../../shared/pipes/title-case-pipe';
import {
  getReviewLockedMessage,
  getReviewUnlockTimestamp,
} from '../../../../shared/utils/result-release.util';
import { OutlineButtonComponent } from '../../../../shared/components/outline-button/outline-button';
import { PrimaryButtonComponent } from '../../../../shared/components/primary-button/primary-button';
import { CalculatorWidgetComponent } from '../../../../shared/components/calculator-widget/calculator-widget';
import { MagnifierWidgetComponent } from '../../../../shared/components/magnifier-widget/magnifier-widget';
import {
  EXAM_INSTRUCTION_SECTIONS,
  GENERAL_INSTRUCTION_ITEMS,
  QUESTION_PALETTE_LEGEND,
} from '../../instructions.data';
import { QuestionPaperPopupComponent } from '../../components/question-paper-popup/question-paper-popup';
import { SubmitExaminationDetailsPopupComponent } from '../../components/submit-examination-details-popup/submit-examination-details-popup';
import { ExamSessionService } from '../../services/exam-session.service';
import { TestSeriesService } from '../../services/test-series.service';
import { StorageService } from '../../../../core/services/storage.service';
import { AuthService } from '../../../auth/services/auth.service';
import { UserProfileData } from '../../../auth/model/auth.model';
import { ToastrService } from 'ngx-toastr';
import { MockTestSecureContentDirective } from '../../../../core/security/mock-test-security.directive';
import { ExamTabLockService } from '../../../../core/security/exam-tab-lock.service';
import { ExamNotificationBlockerService } from '../../../../core/security/exam-notification-blocker.service';
import { MockTestSecurityService } from '../../../../core/security/mock-test-security.service';
import { SecurityViolationType } from '../../../../core/security/mock-test-security.model';
import { isTouchDevice } from '../../../../core/security/touch-device.util';
import { ExamEventTrackerService } from '../../../../core/services/exam-event-tracker.service';
import {
  ExamEnvironmentMonitorService,
  SecurityMonitoringStopReason,
} from '../../../../core/security/exam-environment-monitor.service';
import { QuestionNavigationMethod } from '../../../../core/models/exam-event.model';
import { ExamLeaveAware } from '../../../../core/guards/exam-leave.guard';
import { RichHtmlPipe } from '../../../../shared/pipes/rich-html.pipe';

const FIXED_SECTION_TIMER_USER_IDS: readonly string[] = [];
const FIXED_SECTION_DURATION_IN_SECONDS = 3 * 60;
/** Background progress save fires once every this many questions committed, not on every single one. */
const PROGRESS_SYNC_QUESTION_INTERVAL = 2;
/** Server PROCTORING_WARNING frames open the warning popup on the very first violation (shown in red)... */
const PROCTORING_FIRST_WARNING = 1;
/** ...and then again on every violation from this count up to the limit (shown in amber). */
const PROCTORING_WARNING_FROM = 5;
/** The proctoring warning closes itself once it has been in view this long in total. */
const PROCTORING_WARNING_AUTO_CLOSE_MS = 5000;
/** How often the open proctoring warning checks whether it's in view. */
const PROCTORING_WARNING_VIEW_POLL_MS = 250;
/** The time reminder closes itself after this long; the countdown keeps running underneath. */
const TIME_WARNING_AUTO_CLOSE_MS = 3000;
/** The security auto-submit popup can't be closed before this. */
const VIOLATION_POPUP_MIN_OPEN_MS = 2000;

/** How the student got to a question: palette click, Save/Mark & Next, or the app moving them (section change). */
type QuestionNavigationTrigger = 'palette' | 'next' | 'auto';

/** One entry of PROCTORING_WARNING.violations, the server's list of every violation counted so far. */
interface ProctoringViolation {
  eventType: string;
  message: string;
  timestamp: string;
}

interface ProctoringWarning {
  violationCount: number;
  message: string;
  /** The violation that raised this warning; null when the frame carried no list. */
  current: ProctoringViolation | null;
  /** The ones before it, newest first. */
  previous: ProctoringViolation[];
}

/** Per-tab copy of the live exam state, written on unload and read back after a reload. */
const RELOAD_SNAPSHOT_KEY = 'examReloadSnapshot';

interface ReloadSnapshot extends ResumeData {
  examId: string;
  attemptId?: string;
  savedAt: number;
}

const NAVIGATION_TRIGGER_TO_METHOD: Record<QuestionNavigationTrigger, QuestionNavigationMethod> = {
  palette: 'PALETTE',
  next: 'NEXT',
  auto: 'AUTO',
};

/**
 * Why the exam is being submitted. Only a submit the client itself decides on
 * reports TEST_SUBMITTED — a server auto-submit is already recorded server-side
 * (TEST_AUTO_SUBMITTED), and a timer expiry is the server's TEST_TIME_EXPIRED,
 * so neither may be passed off as a voluntary submit (FINAL_ANSWER_RECORDED.endedBy).
 */
type SubmitTrigger = 'manual' | 'security' | 'time-expired' | 'server';

@Component({
  selector: 'app-question-paper',
  imports: [
    RichHtmlPipe,
    DatePipe,
    TitleCasePipe,
    PrimaryButtonComponent,
    OutlineButtonComponent,
    CalculatorWidgetComponent,
    MagnifierWidgetComponent,
    QuestionPaperPopupComponent,
    SubmitExaminationDetailsPopupComponent,
    MockTestSecureContentDirective,
  ],
  templateUrl: './question-paper.html',
  styleUrl: './question-paper.css',
})
export class QuestionPaper implements OnInit, OnDestroy, ExamLeaveAware {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly examSessionService = inject(ExamSessionService);
  private readonly testSeriesService = inject(TestSeriesService);
  private readonly storageService = inject(StorageService);
  private readonly authService = inject(AuthService);
  private readonly toastr = inject(ToastrService);
  private readonly securityService = inject(MockTestSecurityService);
  private readonly tabLock = inject(ExamTabLockService);
  private readonly notificationBlocker = inject(ExamNotificationBlockerService);
  private readonly eventTracker = inject(ExamEventTrackerService);
  private readonly environmentMonitor = inject(ExamEnvironmentMonitorService);
  private hadFullscreen = false;
  private hasReportedFocusLoss = false;
  // WINDOW_BLURRED / WINDOW_FOCUSED come from the security service's combined
  // focus signal rather than raw window blur/focus, so the notification shade
  // or quick settings pulled over the exam (which the browser often doesn't
  // announce as a blur) is reported too, and only real changes are sent.
  private readonly examFocusSubscription = this.securityService.examFocusChanges$
    .pipe(takeUntilDestroyed())
    .subscribe((focused) => {
      this.eventTracker.track(focused ? 'WINDOW_FOCUSED' : 'WINDOW_BLURRED');
      this.handleFocusChange();
    });
  private submitTimerId: ReturnType<typeof setTimeout> | null = null;
  private hasHandledSecurityTermination = false;
  private hasSubmittedOnUnload = false;
  private hasExitedOnTabSwitch = false;
  private isLeavingExam = false;
  private serverEventsSubscription: Subscription | null = null;
  /**
   * The backend's authoritative submittedAt/reviewAvailableAt (ReviewAvailabilityCalculator),
   * captured from a SUBMISSION_RESULT/TEST_AUTO_SUBMITTED frame as soon as it arrives. Used by
   * buildExamResultState so a server-triggered auto-submit (duration expiry, violation limit)
   * shows the real review-unlock time instead of a client-side estimate. A manual Submit click
   * can't rely on this: endEventTrackingSession unsubscribes from server events the moment
   * TEST_SUBMITTED is sent, before the server's SUBMISSION_RESULT frame can arrive — that path
   * still estimates locally.
   */
  private lastServerSubmissionResult: { submittedAt?: string | null; reviewAvailableAt?: string | null } | null = null;
  private screenEntryTimestamp = 0;
  private isPostingProgressSnapshot = false;
  private questionsCommittedSinceLastSync = 0;
  private resizeDebounceTimerId: ReturnType<typeof setTimeout> | null = null;
  private violationPopupTimerId: ReturnType<typeof setTimeout> | null = null;
  private hasEndedEventSession = false;

  private readonly securityTerminationEffect = effect(() => {
    if (this.securityService.terminationRequested() === 0 || this.hasHandledSecurityTermination) {
      return;
    }

    this.hasHandledSecurityTermination = true;
    this.submitExam(true, 'security');

    // The lock overlay is mounted at the app root, above whatever route we
    // navigate to next (exam-loading / examination-submitted). Auto-dismiss
    // it shortly after so it doesn't sit on top of those screens forever;
    // the overlay's own "Go to Test Series" button covers the immediate case.
    setTimeout(() => this.securityService.resolveLock(), 2000);
  });

  protected readonly preparedState = this.examSessionService.preparedQuestionPaperState;
  protected readonly parts = computed(() => this.preparedState()?.parts ?? []);
  protected readonly questions = computed(() => this.preparedState()?.questions ?? []);
  protected readonly popupHeading = computed(
    () => this.preparedState()?.examTitle ?? 'Examination',
  );
  protected readonly instructionIntroItems = computed(() => [
    {
      ...GENERAL_INSTRUCTION_ITEMS[0],
      text: `Total duration of ${this.popupHeading()} is ${this.parts().reduce((sum, part) => sum + part.durationInMinutes, 0)} minutes.`,
    },
    GENERAL_INSTRUCTION_ITEMS[1],
    GENERAL_INSTRUCTION_ITEMS[2],
  ]);
  protected readonly instructionLegend = QUESTION_PALETTE_LEGEND;
  protected readonly imageEnum = ImageEnum;
  protected readonly activePartIndex = signal(0);
  protected readonly activeQuestionIndex = signal(0);
  protected readonly partTimeLeftInSeconds = signal(0);
  protected readonly totalTimeLeftInSeconds = signal(0);

  protected readonly popupMode = signal<
    'fullscreen-exit' | 'time-expired' | 'section-time-locked' | null
  >(null);
  /** Shown once the server ends the attempt for proctoring violations; sits above every other popup. */
  protected readonly isViolationSubmitOpen = signal(false);
  /** The open PROCTORING_WARNING popup; it closes on Okay or after PROCTORING_WARNING_AUTO_CLOSE_MS in view. */
  protected readonly proctoringWarning = signal<ProctoringWarning | null>(null);
  private readonly proctoringWarningDialog = viewChild<ElementRef<HTMLElement>>('proctoringWarningDialog');
  /**
   * Only time the student can actually see the warning counts towards the
   * auto-close, so it isn't missed while the exam is blurred or covered by
   * the portrait / security-lock / duplicate-tab overlay. A newer warning
   * starts its own count.
   */
  private readonly proctoringWarningAutoCloseEffect = effect((onCleanup) => {
    const warning = this.proctoringWarning();

    if (!warning) {
      return;
    }

    let viewedMs = 0;
    const pollId = setInterval(() => {
      if (!this.isProctoringWarningInView()) {
        return;
      }

      viewedMs += PROCTORING_WARNING_VIEW_POLL_MS;

      // Checked against this warning, so a tick that lands just before the
      // effect is cleaned up can't close a newer one.
      if (viewedMs >= PROCTORING_WARNING_AUTO_CLOSE_MS && this.proctoringWarning() === warning) {
        this.closeProctoringWarning();
      }
    }, PROCTORING_WARNING_VIEW_POLL_MS);

    onCleanup(() => clearInterval(pollId));
  });
  /**
   * The violation count at which the attempt is auto-submitted and synced —
   * the latest `maxViolationCount` from the server's PROCTORING_WARNING
   * frames; null until the first frame arrives.
   */
  protected readonly proctoringViolationLimit = signal<number | null>(null);
  protected readonly proctoringFirstWarning = PROCTORING_FIRST_WARNING;
  /** The violation popup can't be dismissed until VIOLATION_POPUP_MIN_OPEN_MS has passed. */
  protected readonly canCloseViolationSubmit = signal(false);
  protected readonly contentPopupMode = signal<QuestionPaperPopupMode | null>(null);
  protected readonly isSubmitPopupOpen = signal(false);
  protected readonly isTimeWarningOpen = signal(false);
  protected readonly selectedAnswers = signal<Record<number, string>>({});
  protected readonly savedAnswers = signal<Record<number, string>>({});
  protected readonly guessedQuestions = signal<Record<number, boolean>>({});
  protected readonly markedForReview = signal<Record<number, boolean>>({});
  protected readonly visitedQuestions = signal<Record<number, boolean>>({});
  protected readonly userProfile = signal<UserProfileData | null>(null);
  protected readonly hoveredPartLabel = signal<string | null>(null);
  protected readonly hoverPopupPosition = signal<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  private timerId: ReturnType<typeof setInterval> | null = null;
  private timeWarningTimerId: ReturnType<typeof setTimeout> | null = null;

  /** Legacy auto-split parts are labelled "Group X"; admin-configured ones are called parts. */
  private readonly partTerm = computed(() =>
    this.parts().every((part) => /^Group [A-Z]$/.test(part.label)) ? 'group' : 'part',
  );
  protected readonly activePart = computed<QuestionPart | null>(
    () => this.parts()[this.activePartIndex()] ?? null,
  );
  protected readonly activePartQuestions = computed(() =>
    this.questions().filter((question) => question.part === this.activePart()?.label),
  );
  protected readonly activeQuestion = computed<QuestionPaperItem | null>(
    () => this.activePartQuestions()[this.activeQuestionIndex()] ?? null,
  );
  protected readonly instructionPopupSections = computed<InstructionSection[]>(() =>
    EXAM_INSTRUCTION_SECTIONS.filter((section) =>
      ['parts', 'navigation', 'answering'].includes(section.id),
    ).map((section) => {
      if (section.id !== 'parts') {
        return section;
      }

      return {
        ...section,
        title: this.partTerm() === 'part' ? 'Parts:' : section.title,
        items: [
          `This test is divided into ${this.parts().length} ${this.partTerm()}${this.parts().length === 1 ? '' : 's'} - ${this.getFormattedPartLabels()}.`,
          `Each ${this.partTerm()} will have ${this.getPartQuestionText()} and is allotted ${this.getPartDurationText()}.`,
          `You cannot proceed to the next ${this.partTerm()} until you complete the allotted time of the previous ${this.partTerm()}.`,
          `The next ${this.partTerm()} will start automatically after the completion of the previous ${this.partTerm()}.`,
          `You cannot review/modify your responses for a ${this.partTerm()} after you complete the allotted time.`,
          `The test will be auto submitted after ${this.parts().reduce((sum, part) => sum + part.durationInMinutes, 0)} minutes.`,
          'You cannot use multiple devices to take this test.',
          'Do not switch to another tab, window, or app, and do not exit full-screen mode during the test. Each time you do, you will receive a warning. After 3 warnings, your test will be automatically submitted.',
        ],
      };
    }),
  );
  protected readonly timeLeft = computed(() => this.formatTime(this.partTimeLeftInSeconds()));
  protected readonly timeWarningLabel = computed(() => {
    const totalSeconds = this.partTimeLeftInSeconds();
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const minuteText = minutes ? `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}` : '';
    const secondText = seconds ? `${seconds} ${seconds === 1 ? 'second' : 'seconds'}` : '';
    const text = [minuteText, secondText].filter(Boolean).join(' ') || '0 seconds';

    return `${text} ${totalSeconds === 1 || (minutes === 1 && !seconds) ? 'is' : 'are'} left`;
  });
  protected readonly canSubmit = computed(() => this.activePartIndex() === this.parts().length - 1);
  protected readonly isLastQuestionOfSection = computed(
    () => this.activeQuestionIndex() === this.activePartQuestions().length - 1,
  );
  protected readonly answeredCount = computed(
    () =>
      this.activePartQuestions().filter(
        (question) => this.hasSavedAnswer(question.id) && !this.isMarked(question.id),
      ).length,
  );
  protected readonly notAnsweredCount = computed(
    () =>
      this.activePartQuestions().filter(
        (question) =>
          this.isVisited(question.id) &&
          !this.hasSavedAnswer(question.id) &&
          !this.isMarked(question.id),
      ).length,
  );
  protected readonly notVisitedCount = computed(
    () => this.activePartQuestions().filter((question) => !this.isVisited(question.id)).length,
  );
  protected readonly markedCount = computed(
    () =>
      this.activePartQuestions().filter(
        (question) => this.isMarked(question.id) && !this.hasSavedAnswer(question.id),
      ).length,
  );
  protected readonly markedAnsweredCount = computed(
    () =>
      this.activePartQuestions().filter(
        (question) => this.isMarked(question.id) && this.hasSavedAnswer(question.id),
      ).length,
  );
  protected readonly totalQuestionCount = computed(() => this.questions().length);
  protected readonly totalAttemptedCount = computed(
    () => this.questions().filter((question) => this.hasSavedAnswer(question.id)).length,
  );
  protected readonly totalAnsweredCount = computed(
    () =>
      this.questions().filter(
        (question) => this.hasSavedAnswer(question.id) && !this.isMarked(question.id),
      ).length,
  );
  protected readonly totalNotAnsweredCount = computed(
    () =>
      this.questions().filter(
        (question) =>
          this.isVisited(question.id) &&
          !this.hasSavedAnswer(question.id) &&
          !this.isMarked(question.id),
      ).length,
  );
  protected readonly totalNotVisitedCount = computed(
    () => this.questions().filter((question) => !this.isVisited(question.id)).length,
  );
  protected readonly totalMarkedCount = computed(
    () =>
      this.questions().filter(
        (question) => this.isMarked(question.id) && !this.hasSavedAnswer(question.id),
      ).length,
  );
  protected readonly totalMarkedForReviewCount = computed(
    () => this.questions().filter((question) => this.isMarked(question.id)).length,
  );
  protected readonly candidateDisplayName = computed(() => this.getCandidateDisplayName());
  protected readonly candidateInitials = computed(() => this.getCandidateInitials());
  private readonly shouldUseFixedSectionTimer = computed(() =>
    FIXED_SECTION_TIMER_USER_IDS.includes(this.getStoredUserId()),
  );
  private readonly totalDurationInSeconds = computed(() =>
    this.parts().reduce((sum, part) => sum + this.getPartDurationInSeconds(part), 0),
  );

  ngOnInit(): void {
    this.authService.getUserDetails().subscribe({
      next: (res) => {
        if (res?.data) {
          this.userProfile.set(res.data);
        }
      },
      error: () => {
        // Keep fallback display name if profile fetch fails.
      },
    });

    if (!this.preparedState()) {
      const examId = this.route.snapshot.paramMap.get('id');

      if (!examId) {
        void this.router.navigate(['/test-series']);
        return;
      }

      // A reload: closing the page paused the attempt on the server, so it is
      // resumed through the start API (409 / deadline errors are toasted by the
      // error interceptor) and its socket opened before loading, so a failed
      // load can still be reported.
      this.examSessionService.resumeAttemptAfterReload().subscribe({
        next: () => this.loadQuestionPaper(examId),
        error: () => void this.router.navigate(['/test-series']),
      });
      return;
    }

    this.initializeExamScreen();
  }

  private loadQuestionPaper(examId: string): void {
    this.examSessionService.ensurePreparedQuestionPaperState(examId).subscribe({
      next: () => {
        this.initializeExamScreen();
      },
      error: () => {
        this.eventTracker.track('QUESTION_LOAD_FAILED');
        this.examSessionService.endEventTracking();
        void this.router.navigate(['/test-series']);
      },
    });
  }

  private initializeExamScreen(): void {
    if (!this.parts().length || !this.questions().length) {
      void this.router.navigate(['/test-series']);
      return;
    }

    // From here on any other tab of the app is blocked by the duplicate-tab overlay.
    this.tabLock.claimExam();
    this.notificationBlocker.block();
    this.screenEntryTimestamp = Date.now();
    this.beginEventTrackingSession();

    // The server had already paused/rejected the attempt; we're on our way out.
    if (this.hasEndedEventSession) {
      return;
    }

    // A reload (which browsers don't let a page block) picks up exactly where
    // the student was, ahead of the server's coarser resume data.
    // No native "Reload site?" prompt is shown (its text can't be changed);
    // the student gets our own message once the page is back instead.
    const reloadSnapshot = this.takeReloadSnapshot();
    const resumeData = reloadSnapshot ?? this.preparedState()?.resumeData;

    if (reloadSnapshot) {
      this.toastr.warning(
        'Page refresh is disabled during the examination. Your answers and timer have been kept.',
        'Refresh disabled',
      );
    }

    if (resumeData) {
      this.activePartIndex.set(resumeData.resumePartIndex);
      this.activeQuestionIndex.set(resumeData.resumeQuestionIndex);
      this.savedAnswers.set(resumeData.savedAnswers);
      this.selectedAnswers.set(resumeData.selectedAnswers);
      this.visitedQuestions.set(resumeData.visitedQuestions);
      this.markedForReview.set(resumeData.markedForReview);
      this.guessedQuestions.set(resumeData.guessedQuestions);
      this.totalTimeLeftInSeconds.set(resumeData.totalTimeLeftInSeconds);
      this.partTimeLeftInSeconds.set(resumeData.partTimeLeftInSeconds);
      this.enforceFullscreenState();
      this.trackSectionEntry();
      this.markCurrentQuestionAsVisited();
      this.startResumedPartTimer();
      this.scrollActivePaletteButtonIntoView();
    } else {
      this.activePartIndex.set(0);
      this.activeQuestionIndex.set(0);
      this.totalTimeLeftInSeconds.set(this.totalDurationInSeconds());
      this.enforceFullscreenState();
      this.trackSectionEntry();
      this.markCurrentQuestionAsVisited();
      this.startPartTimer();
      this.scrollActivePaletteButtonIntoView();
    }
  }

  private beginEventTrackingSession(): void {
    const attemptId = this.examSessionService.activeExamMeta()?.attemptId;
    const testId = this.preparedState()?.examId;

    if (!attemptId || !testId) {
      console.warn('[ExamEventTracker] not connecting: missing attemptId or testId', {
        attemptId,
        testId,
      });
      return;
    }

    // Usually already open from the Start Test popup; beginSession only
    // connects (and reports SESSION_STARTED) after a reload.
    this.eventTracker.beginSession({
      attemptId,
      studentId: this.getStoredUserId(),
      testId,
    });

    this.serverEventsSubscription?.unsubscribe();
    this.serverEventsSubscription = this.eventTracker.serverEvents$.subscribe((event) =>
      this.handleServerEvent(event.eventType, event.payload ?? {}),
    );

    this.environmentMonitor.start();

    // The socket is opened from the Start Test popup / instructions, so the
    // server may already have paused or rejected it before this subscription.
    const sessionStop = this.eventTracker.getSessionStop(attemptId);

    if (sessionStop) {
      this.handleServerEvent(sessionStop.eventType, sessionStop.payload ?? {});
    }
  }

  /**
   * Runs once, when the student leaves the exam (submit, exit, tab close, or
   * the page being destroyed). Order matters to the server: the open
   * question and section are closed before the attempt-level event, then
   * monitoring stops and the session ends. Once the attempt is over the
   * tracker also runs the TEST_SYNC_STARTED → TEST_SYNC_COMPLETED handshake,
   * which triggers the server's sync finalization + derivation right away.
   */
  private endEventTrackingSession(
    reason: SecurityMonitoringStopReason,
    attemptEvent: 'TEST_SUBMITTED' | 'TEST_EXITED' | null = null,
  ): void {
    if (this.hasEndedEventSession) {
      return;
    }

    this.hasEndedEventSession = true;
    this.serverEventsSubscription?.unsubscribe();
    this.serverEventsSubscription = null;

    const question = this.activeQuestion();

    if (question) {
      this.trackQuestionExit(question);
    }

    this.trackSectionExit();

    if (attemptEvent) {
      this.eventTracker.track(attemptEvent);
    }

    this.environmentMonitor.stop(reason);
    this.eventTracker.endSession({ finalSync: reason === 'submitted' });
  }

  /**
   * Reacts to frames the server pushes back over the same socket. Only
   * TEST_AUTO_SUBMITTED currently drives client behavior (the server decided
   * the attempt is over, so stop letting the student keep answering it);
   * everything else — SUBMISSION_RESULT included — is left for the existing
   * REST-driven submit/result flow and just logged for now.
   */
  private handleServerEvent(eventType: string, payload: Record<string, unknown>): void {
    if (eventType === 'PROCTORING_WARNING') {
      this.handleProctoringWarning(payload);
      return;
    }

    if (eventType === 'SUBMISSION_RESULT') {
      this.captureServerSubmissionResult(payload);
      return;
    }

    if (eventType === 'TEST_AUTO_SUBMITTED') {
      this.captureServerSubmissionResult(payload);
      if (payload['reason'] === 'VIOLATION_LIMIT_EXCEEDED') {
        this.handleViolationAutoSubmit();
      } else {
        this.handleServerAutoSubmit();
      }
      return;
    }

    if (eventType === 'SESSION_REPLACED') {
      this.handleSessionStopped(
        'Session moved to another device',
        this.readFrameMessage(payload) ??
          'You have been logged in from another device. This test session has been paused.',
      );
      return;
    }

    if (eventType === 'TEST_PAUSED') {
      // The frame's own message ("TEST_PAUSED: ...") isn't meant for students.
      this.handleSessionStopped(
        'Test paused',
        payload['reason'] === 'INACTIVITY'
          ? 'Your test was paused after 5 minutes of inactivity. You can resume it from Test Series.'
          : 'Your test has been paused. You can resume it from Test Series.',
      );
      return;
    }

    if (eventType === 'CONNECTION_REJECTED') {
      this.handleSessionStopped('Unable to continue test', this.getConnectionRejectedMessage(payload));
      return;
    }

    console.debug(`[QuestionPaper] unhandled server exam event: ${eventType}`, payload);
  }

  /** See lastServerSubmissionResult's doc comment for why this only ends up used by auto-submit. */
  private captureServerSubmissionResult(payload: Record<string, unknown>): void {
    const result = payload['result'] as { submittedAt?: string | null; reviewAvailableAt?: string | null } | undefined;
    if (result) {
      this.lastServerSubmissionResult = result;
    }
  }

  private readFrameMessage(payload: Record<string, unknown>): string | null {
    return typeof payload['message'] === 'string' && payload['message'] ? payload['message'] : null;
  }

  private getConnectionRejectedMessage(payload: Record<string, unknown>): string {
    switch (payload['code']) {
      case 'OTHER_EXAM_IN_PROGRESS':
        return (
          this.readFrameMessage(payload) ??
          'Another exam is already in progress. Finish or leave it before starting this one.'
        );
      case 'ATTEMPT_FINALIZED':
        return 'This test has already been submitted.';
      case 'ATTEMPT_NOT_FOUND':
        return 'This test attempt could not be found. Please start the test again.';
      default:
        return this.readFrameMessage(payload) ?? 'This test can no longer be continued here.';
    }
  }

  /**
   * The server has stopped this attempt's socket for good: the attempt was
   * opened in another window/device (SESSION_REPLACED), paused for
   * inactivity/exit (TEST_PAUSED) or isn't allowed to run here
   * (CONNECTION_REJECTED). Leave the exam straight away without saving
   * progress or reporting TEST_EXITED — the attempt is paused (or belongs to
   * another session) on the server, and anything sent from here would
   * overwrite it. The student resumes through the start API from Test Series.
   */
  private handleSessionStopped(title: string, message: string): void {
    if (this.hasEndedEventSession) {
      return;
    }

    this.hasEndedEventSession = true;
    this.serverEventsSubscription?.unsubscribe();
    this.serverEventsSubscription = null;

    this.popupMode.set(null);
    this.proctoringWarning.set(null);
    this.hideTimeWarning();

    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }

    if (this.submitTimerId) {
      clearTimeout(this.submitTimerId);
      this.submitTimerId = null;
    }

    this.environmentMonitor.stop('exited');
    this.eventTracker.disconnect();

    try {
      sessionStorage.removeItem(RELOAD_SNAPSHOT_KEY);
    } catch {
      // Storage unavailable — nothing to clear.
    }

    this.toastr.error(message, title);
    void this.router.navigate(['/test-series']);
  }

  /**
   * The server counts violations and sends `maxViolationCount` with each
   * frame. The student gets a popup they have to acknowledge on the 1st
   * violation (red) and on every one from the 5th up to maxViolationCount - 1
   * (amber); violations 2–4 are recorded silently. When violationCount reaches
   * maxViolationCount the test is completed: auto-submit popup, plus the
   * TEST_SYNC_COMPLETED handshake. The server's TEST_AUTO_SUBMITTED
   * (VIOLATION_LIMIT_EXCEEDED) is handled the same way if it comes first.
   * The popup lists the violation that raised it and every one before it,
   * from the frame's `violations`.
   */
  private handleProctoringWarning(payload: Record<string, unknown>): void {
    const violationCount = Number(payload['violationCount']);
    const message =
      typeof payload['message'] === 'string' && payload['message']
        ? payload['message']
        : 'Proctoring violations detected. Continued violations will auto-submit your test.';

    if (!Number.isFinite(violationCount)) {
      return;
    }

    const maxViolationCount = Number(payload['maxViolationCount']);

    if (Number.isFinite(maxViolationCount) && maxViolationCount > 0) {
      this.proctoringViolationLimit.set(maxViolationCount);
    }

    const limit = this.proctoringViolationLimit();

    if (limit !== null && violationCount >= limit) {
      this.handleViolationAutoSubmit();
      return;
    }

    if (violationCount !== PROCTORING_FIRST_WARNING && violationCount < PROCTORING_WARNING_FROM) {
      return;
    }

    const violations = this.readProctoringViolations(payload['violations']);

    // A newer warning arriving while one is open just replaces it.
    this.proctoringWarning.set({
      violationCount,
      message,
      current: violations.at(-1) ?? null,
      previous: violations.slice(0, -1).reverse(),
    });
  }

  /**
   * The frame's `violations` in the server's order (oldest first, so the last
   * one raised this warning); malformed entries are skipped. An unparseable
   * timestamp is blanked, since the date pipe throws on one.
   */
  private readProctoringViolations(value: unknown): ProctoringViolation[] {
    if (!Array.isArray(value)) {
      return [];
    }

    const readString = (entry: Record<string, unknown>, key: string): string =>
      typeof entry[key] === 'string' ? entry[key] : '';

    return value
      .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
      .map((entry) => {
        const timestamp = readString(entry, 'timestamp');

        return {
          eventType: readString(entry, 'eventType'),
          message: readString(entry, 'message'),
          timestamp: Number.isNaN(Date.parse(timestamp)) ? '' : timestamp,
        };
      })
      .filter((violation) => violation.eventType || violation.message);
  }

  protected closeProctoringWarning(): void {
    this.proctoringWarning.set(null);
  }

  /**
   * The exam isn't blurred, and the warning is what's on top at its own
   * centre — not one of the full-screen overlays the app can raise over the
   * question paper.
   */
  private isProctoringWarningInView(): boolean {
    const dialog = this.proctoringWarningDialog()?.nativeElement;

    if (!dialog || this.securityService.isObscured()) {
      return false;
    }

    const rect = dialog.getBoundingClientRect();
    const x = Math.min(Math.max(rect.left + rect.width / 2, 0), window.innerWidth - 1);
    const y = Math.min(Math.max(rect.top + rect.height / 2, 0), window.innerHeight - 1);
    const topmost = document.elementFromPoint(x, y);

    return topmost !== null && dialog.contains(topmost);
  }

  /**
   * The server has already submitted the attempt. Stop the exam, show the
   * security popup (it can't be closed for VIOLATION_POPUP_MIN_OPEN_MS), and
   * hand the student over to the normal submission screen when they close it.
   */
  private handleViolationAutoSubmit(): void {
    if (this.isViolationSubmitOpen()) {
      return;
    }

    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }

    if (this.submitTimerId) {
      clearTimeout(this.submitTimerId);
      this.submitTimerId = null;
    }

    this.isSubmitPopupOpen.set(false);
    this.hideTimeWarning();
    this.popupMode.set(null);
    this.proctoringWarning.set(null);
    this.canCloseViolationSubmit.set(false);
    this.isViolationSubmitOpen.set(true);

    // The attempt is over: close the open question/section and run the
    // TEST_SYNC_STARTED → TEST_SYNC_COMPLETED handshake now rather than
    // waiting for the student to close the popup. submitExam() later skips
    // this because the session has already ended.
    this.endEventTrackingSession('submitted');

    this.violationPopupTimerId = setTimeout(() => {
      this.violationPopupTimerId = null;
      this.canCloseViolationSubmit.set(true);
    }, VIOLATION_POPUP_MIN_OPEN_MS);
  }

  protected closeViolationSubmit(): void {
    if (!this.canCloseViolationSubmit()) {
      return;
    }

    this.isViolationSubmitOpen.set(false);
    this.submitExam(true, 'server');
  }

  private handleServerAutoSubmit(): void {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }

    if (this.submitTimerId) {
      clearTimeout(this.submitTimerId);
      this.submitTimerId = null;
    }

    this.popupMode.set('time-expired');
    this.submitExam(true, 'server');
  }

  private enforceFullscreenState(): void {
    this.hadFullscreen = Boolean(document.fullscreenElement);

    if (!document.fullscreenElement) {
      this.popupMode.set('fullscreen-exit');
    }
  }

  ngOnDestroy(): void {
    if (this.timerId) {
      clearInterval(this.timerId);
    }

    if (this.timeWarningTimerId) {
      clearTimeout(this.timeWarningTimerId);
    }

    if (this.violationPopupTimerId) {
      clearTimeout(this.violationPopupTimerId);
    }

    if (this.submitTimerId) {
      clearTimeout(this.submitTimerId);
    }

    if (this.resizeDebounceTimerId) {
      clearTimeout(this.resizeDebounceTimerId);
    }

    this.endEventTrackingSession('navigated_away');
    this.tabLock.releaseExam();
    this.notificationBlocker.unblock();
  }

  protected selectPart(partIndex: number): void {
    if (partIndex !== this.activePartIndex()) {
      return;
    }
  }

  protected isPartDisabled(partIndex: number): boolean {
    return partIndex !== this.activePartIndex();
  }

  protected setActiveQuestion(index: number, via: QuestionNavigationTrigger = 'palette'): void {
    const question = this.activePartQuestions()[index];

    if (!question) {
      return;
    }

    const previousQuestion = this.activeQuestion();
    const isLeavingQuestion = index !== this.activeQuestionIndex();

    if (isLeavingQuestion && previousQuestion) {
      this.trackQuestionExit(previousQuestion);
    }

    this.discardUnsavedAnswer(previousQuestion?.id);

    this.activeQuestionIndex.set(index);
    this.scrollActivePaletteButtonIntoView();

    // Every question transition counts toward the sync interval — whether the
    // student saved an answer, marked for review, or just skipped ahead via
    // the palette without answering.
    if (isLeavingQuestion) {
      this.trackQuestionEntry(question);

      this.trackNavigation(via, previousQuestion, question);

      this.syncProgressEveryFewQuestions();
    } else {
      this.markQuestionAsVisited(question.id);
    }
  }

  private trackNavigation(
    via: QuestionNavigationTrigger,
    fromQuestion: QuestionPaperItem | null | undefined,
    toQuestion: QuestionPaperItem | null | undefined,
  ): void {
    this.eventTracker.track('QUESTION_NAVIGATED', {
      method: NAVIGATION_TRIGGER_TO_METHOD[via],
      fromQuestionId: fromQuestion?.sourceQuestionId ?? null,
      toQuestionId: toQuestion?.sourceQuestionId ?? null,
    });
  }

  private discardUnsavedAnswer(questionId: number | undefined): void {
    if (questionId === undefined) {
      return;
    }

    const savedOptionId = this.savedAnswers()[questionId];

    this.selectedAnswers.update((answers) => {
      const nextAnswers = { ...answers };

      if (savedOptionId) {
        nextAnswers[questionId] = savedOptionId;
      } else {
        delete nextAnswers[questionId];
      }

      return nextAnswers;
    });
  }

  protected selectAnswer(questionId: number, optionId: string): void {
    this.markQuestionAsVisited(questionId);
    this.trackOptionSelection(questionId, optionId);

    this.selectedAnswers.update((answers) => ({
      ...answers,
      [questionId]: optionId,
    }));
  }

  protected clearResponse(): void {
    const questionId = this.activeQuestion()?.id;

    if (!questionId) {
      return;
    }

    this.selectedAnswers.update((answers) => {
      const nextAnswers = { ...answers };
      delete nextAnswers[questionId];
      return nextAnswers;
    });

    this.guessedQuestions.update((guessed) => {
      const nextGuessed = { ...guessed };
      delete nextGuessed[questionId];
      return nextGuessed;
    });

    this.eventTracker.track(
      'ANSWER_CLEARED',
      {},
      { questionId: this.activeQuestion()?.sourceQuestionId ?? null },
    );
  }

  // OPTION_CHANGED, FIRST_INTERACTION and FIRST_OPTION_SELECTED are derived
  // server-side from this event's previousOptionId, so only this is sent.
  private trackOptionSelection(questionId: number, optionId: string): void {
    const question = this.questions().find((candidate) => candidate.id === questionId);
    const previousOptionId = this.selectedAnswers()[questionId];

    this.eventTracker.track(
      'OPTION_SELECTED',
      {
        optionId: this.getOptionLabel(question, optionId),
        previousOptionId: previousOptionId ? this.getOptionLabel(question, previousOptionId) : null,
      },
      { questionId: question?.sourceQuestionId ?? null },
    );
  }

  private getOptionLabel(question: QuestionPaperItem | undefined, optionId: string): string {
    const label = question?.options.find((option) => option.id === optionId)?.label;
    // Option ids are `${sourceQuestionId}-${key}`, so the key is still
    // recoverable if the lookup misses — never send the raw id.
    return (label ?? optionId.split('-').pop() ?? optionId).toUpperCase();
  }

  protected toggleGuessing(event?: Event): void {
    const questionId = this.activeQuestion()?.id;

    if (!questionId) {
      return;
    }

    // Only allow guessing if the user has selected an answer.
    // If they are unchecking the guess, we allow it.
    const isCurrentlyGuessed = this.guessedQuestions()[questionId];

    if (!isCurrentlyGuessed && !this.hasSelectedAnswer(questionId)) {
      event?.preventDefault();
      this.toastr.warning(
        'Please select an answer option before marking the question as a guessed answer.',
      );
      return;
    }

    // Determine the new state
    const isNowGuessed = !isCurrentlyGuessed;

    // Update local state
    this.guessedQuestions.update((guessed) => ({
      ...guessed,
      [questionId]: isNowGuessed,
    }));

    // Send event to socket
    const question: any = this.questions().find((candidate) => candidate.id === questionId);

    // console.log(question);

    this.eventTracker.track(
      isNowGuessed ? 'MARKED_AS_GUESS' : 'UNMARKED_AS_GUESS',
      {},
      {
        questionId: question?.sourceQuestionId ?? null,
        subjectId: question?.sourceQuestionId ?? null,
        topicId: question?.topicId ?? null,
      },
    );
  }

  // protected toggleGuessing(event?: Event): void {
  //   const questionId = this.activeQuestion()?.id;

  //   if (!questionId) {
  //     return;
  //   }

  //   // Only allow guessing if the user has selected an answer.
  //   // If they are unchecking the guess, we allow it. But to check it, they must have an answer.
  //   const isCurrentlyGuessed = this.guessedQuestions()[questionId];
  //   if (!isCurrentlyGuessed && !this.hasSelectedAnswer(questionId)) {
  //     event?.preventDefault();
  //     this.toastr.warning('Please select an answer option before marking the question as a guessed answer.');
  //     return;
  //   }

  //   this.guessedQuestions.update((guessed) => ({
  //     ...guessed,
  //     [questionId]: !guessed[questionId],
  //   }));
  // }

  // The full question paper popup is the exam's reference panel.
  protected openContentPopup(mode: QuestionPaperPopupMode): void {
    this.contentPopupMode.set(mode);
    this.eventTracker.track(
      mode === 'instructions' ? 'INSTRUCTIONS_OPENED' : 'REFERENCE_PANEL_OPENED',
      {},
      mode === 'instructions'
        ? {}
        : { questionId: this.activeQuestion()?.sourceQuestionId ?? null },
    );
  }

  protected closeContentPopup(): void {
    const mode = this.contentPopupMode();

    if (mode) {
      this.eventTracker.track(
        mode === 'instructions' ? 'INSTRUCTIONS_CLOSED' : 'REFERENCE_PANEL_CLOSED',
        {},
        mode === 'instructions'
          ? {}
          : { questionId: this.activeQuestion()?.sourceQuestionId ?? null },
      );
    }

    this.contentPopupMode.set(null);
  }

  protected onMagnifierToggled(active: boolean): void {
    if (active) {
      this.eventTracker.track(
        'IMAGE_ZOOMED',
        {},
        { questionId: this.activeQuestion()?.sourceQuestionId ?? null },
      );
    }
  }

  protected openSubmitPopup(): void {
    if (!this.canSubmit()) {
      return;
    }

    this.isSubmitPopupOpen.set(true);
    this.eventTracker.track('TEST_SUBMIT_DIALOG_OPENED');
  }

  protected closeSubmitPopup(): void {
    this.isSubmitPopupOpen.set(false);
    this.eventTracker.track('TEST_SUBMIT_DIALOG_CANCELLED');
  }

  protected closePopup(): void {
    if (this.popupMode() === 'time-expired') {
      return;
    }

    // For 'section-time-locked' the student stays on the current (last) question of the section.
    this.popupMode.set(null);
  }

  /** Closes the reminder (Okay or the 3s auto-close); the countdown was never paused. */
  protected closeTimeWarning(): void {
    this.hideTimeWarning();
  }

  /** Shows the reminder without pausing the countdown; it closes itself after TIME_WARNING_AUTO_CLOSE_MS. */
  private openTimeWarning(remainingSeconds: number): void {
    this.hideTimeWarning();
    this.isTimeWarningOpen.set(true);
    this.eventTracker.track('TEST_TIME_WARNING_SHOWN', { remainingMs: remainingSeconds * 1000 });
    this.timeWarningTimerId = setTimeout(() => this.closeTimeWarning(), TIME_WARNING_AUTO_CLOSE_MS);
  }

  /** Hides the reminder without resuming the countdown. */
  private hideTimeWarning(): void {
    if (this.timeWarningTimerId) {
      clearTimeout(this.timeWarningTimerId);
      this.timeWarningTimerId = null;
    }

    this.isTimeWarningOpen.set(false);
  }

  protected async resumeFullScreen(): Promise<void> {
    if (this.popupMode() === 'time-expired') {
      return;
    }

    this.popupMode.set(null);

    const rootElement = document.documentElement;

    if (!rootElement.requestFullscreen) {
      return;
    }

    try {
      await rootElement.requestFullscreen();
      this.hadFullscreen = true;
      this.eventTracker.track('FULLSCREEN_ENTERED');
    } catch {
      this.popupMode.set('fullscreen-exit');
    }
  }

  protected exitToTestSeries(): void {
    this.popupMode.set(null);
    this.hideTimeWarning();

    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }

    const testId = this.route.snapshot.paramMap.get('id');

    if (!testId) {
      void this.router.navigate(['/test-series']);
      return;
    }

    this.examSessionService.setPendingSubmission(
      this.buildSubmitPayload('IN_PROGRESS'),
      this.buildExamResultState('IN_PROGRESS'),
    );

    this.endEventTrackingSession('exited', 'TEST_EXITED');

    void this.router.navigate(['/test-series', testId, 'exam-loading'], {
      queryParams: { mode: 'submit' },
      replaceUrl: true,
    });
  }

  protected markForReviewAndNext(): void {
    const questionId = this.activeQuestion()?.id;

    if (!questionId) {
      return;
    }

    this.markQuestionAsVisited(questionId);
    // A selected option still counts for evaluation, so it is confirmed like Save & Next.
    this.commitCurrentAnswer(questionId);

    this.markedForReview.update((marked) => ({
      ...marked,
      [questionId]: true,
    }));

    // Sent on every Mark for Review click, with or without a selected option.
    const selectedOptionId = this.selectedAnswers()[questionId] ?? null;
    const markedQuestion = this.questions().find((candidate) => candidate.id === questionId);
    this.eventTracker.track(
      'QUESTION_MARKED_FOR_REVIEW',
      { optionId: selectedOptionId ? this.getOptionLabel(markedQuestion, selectedOptionId) : null },
      { questionId: markedQuestion?.sourceQuestionId ?? null },
    );

    if (this.activeQuestionIndex() === this.activePartQuestions().length - 1) {
      this.popupMode.set('section-time-locked');
      return;
    }

    this.moveToNextQuestion();
  }

  protected saveAndNext(): void {
    const questionId = this.activeQuestion()?.id;

    if (!questionId) {
      return;
    }

    this.markQuestionAsVisited(questionId);
    this.commitCurrentAnswer(questionId);

    const wasMarked = this.isMarked(questionId);

    this.markedForReview.update((marked) => ({
      ...marked,
      [questionId]: false,
    }));

    if (wasMarked) {
      this.eventTracker.track(
        'QUESTION_UNMARKED_FOR_REVIEW',
        {},
        { questionId: this.activeQuestion()?.sourceQuestionId ?? null },
      );
    }

    if (this.activeQuestionIndex() === this.activePartQuestions().length - 1) {
      this.popupMode.set('section-time-locked');
      return;
    }

    this.moveToNextQuestion();
  }

  protected submitExam(force = false, trigger: SubmitTrigger = 'manual'): void {
    if (!force && !this.canSubmit()) {
      return;
    }

    const testId = this.route.snapshot.paramMap.get('id');

    this.isSubmitPopupOpen.set(false);
    this.hideTimeWarning();

    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }

    // The exam clock has run out (locally or per the server's TEST_AUTO_SUBMITTED),
    // so any seconds still on the local countdown are drift: the full duration counts as taken.
    if (trigger === 'time-expired' || this.popupMode() === 'time-expired') {
      this.totalTimeLeftInSeconds.set(0);
      this.partTimeLeftInSeconds.set(0);
    }

    if (!testId) {
      void this.router.navigate(['/test-series']);
      return;
    }

    this.examSessionService.setPendingSubmission(
      this.buildSubmitPayload('COMPLETED'),
      this.buildExamResultState('COMPLETED'),
    );

    const isClientSubmit = trigger === 'manual' || trigger === 'security';
    this.endEventTrackingSession('submitted', isClientSubmit ? 'TEST_SUBMITTED' : null);

    void this.router.navigate(['/test-series', testId, 'exam-loading'], {
      queryParams: { mode: 'submit' },
      replaceUrl: true,
    });
  }

  private goToNextPart(): void {
    const previousQuestion = this.activeQuestion();

    if (previousQuestion) {
      this.trackQuestionExit(previousQuestion);
    }

    this.trackSectionExit();

    // The "Cannot Move Forward" lock only applies to the section that just ended.
    if (this.popupMode() === 'section-time-locked') {
      this.popupMode.set(null);
    }

    const nextPartIndex = this.activePartIndex() + 1;
    this.activePartIndex.set(nextPartIndex);
    this.activeQuestionIndex.set(0);
    this.trackSectionEntry();
    this.markCurrentQuestionAsVisited();
    this.trackNavigation('auto', previousQuestion, this.activeQuestion());
    this.startPartTimer();
    this.scrollActivePaletteButtonIntoView();

    // Force a save on every section change, regardless of where the
    // every-N-questions counter currently stands — a section's last answer
    // shouldn't wait on the next section's questions to get flushed.
    this.postProgressSnapshot();
  }

  protected hasSelectedAnswer(questionId: number): boolean {
    return Boolean(this.selectedAnswers()[questionId]);
  }

  protected hasSavedAnswer(questionId: number): boolean {
    return Boolean(this.savedAnswers()[questionId]);
  }

  protected isOptionSelected(questionId: number, optionId: string): boolean {
    return this.selectedAnswers()[questionId] === optionId;
  }

  protected isGuessing(questionId: number): boolean {
    return Boolean(this.guessedQuestions()[questionId]);
  }

  protected showPartHoverPopup(event: MouseEvent, partLabel: string): void {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const popupWidth = 256;
    const viewportMargin = 8;
    const maxLeft = window.innerWidth - popupWidth - viewportMargin;
    const left = Math.min(Math.max(rect.left, viewportMargin), Math.max(maxLeft, viewportMargin));

    this.hoverPopupPosition.set({ top: rect.bottom + 4, left });
    this.hoveredPartLabel.set(partLabel);
  }

  protected hidePartHoverPopup(): void {
    this.hoveredPartLabel.set(null);
  }

  protected getPartStats(partLabel: string): {
    answered: number;
    notAnswered: number;
    notVisited: number;
    marked: number;
    markedAnswered: number;
  } {
    const partQuestions = this.questions().filter((question) => question.part === partLabel);

    return {
      answered: partQuestions.filter(
        (question) => this.hasSavedAnswer(question.id) && !this.isMarked(question.id),
      ).length,
      notAnswered: partQuestions.filter(
        (question) =>
          this.isVisited(question.id) &&
          !this.hasSavedAnswer(question.id) &&
          !this.isMarked(question.id),
      ).length,
      notVisited: partQuestions.filter((question) => !this.isVisited(question.id)).length,
      marked: partQuestions.filter(
        (question) => this.isMarked(question.id) && !this.hasSavedAnswer(question.id),
      ).length,
      markedAnswered: partQuestions.filter(
        (question) => this.isMarked(question.id) && this.hasSavedAnswer(question.id),
      ).length,
    };
  }

  protected getPaletteState(questionId: number): PaletteState {
    const visited = this.isVisited(questionId);
    const marked = this.isMarked(questionId);
    const answered = this.hasSavedAnswer(questionId);

    if (marked && answered) {
      return 'marked-answered';
    }

    if (marked) {
      return 'marked';
    }

    if (answered) {
      return 'answered';
    }

    if (visited) {
      return 'not-answered';
    }

    return 'not-visited';
  }

  protected getPaletteClasses(questionId: number): string {
    const state = this.getPaletteState(questionId);

    if (state === 'not-visited') {
      return 'instruction-chip instruction-chip-default';
    }

    if (state === 'answered') {
      return 'instruction-chip instruction-chip-green';
    }

    if (state === 'not-answered') {
      return 'instruction-chip instruction-chip-red';
    }

    if (state === 'marked') {
      return 'instruction-chip instruction-chip-purple';
    }

    if (state === 'marked-answered') {
      return 'instruction-chip instruction-chip-violet';
    }

    return '';
  }

  protected isCurrentQuestion(questionId: number): boolean {
    return this.activeQuestion()?.id === questionId;
  }

  protected getLegendShapeClasses(
    state: 'answered' | 'not-answered' | 'not-visited' | 'marked' | 'marked-answered',
  ): string {
    if (state === 'answered') {
      return 'instruction-chip instruction-chip-green';
    }

    if (state === 'not-answered') {
      return 'instruction-chip instruction-chip-red';
    }

    if (state === 'marked') {
      return 'instruction-chip instruction-chip-purple';
    }

    if (state === 'marked-answered') {
      return 'instruction-chip instruction-chip-violet';
    }

    return '';
  }

  private isVisited(questionId: number): boolean {
    return Boolean(this.visitedQuestions()[questionId]);
  }

  private isMarked(questionId: number): boolean {
    return Boolean(this.markedForReview()[questionId]);
  }

  private moveToNextQuestion(): void {
    const nextIndex = this.activeQuestionIndex() + 1;

    if (nextIndex < this.activePartQuestions().length) {
      this.setActiveQuestion(nextIndex, 'next');
    }
  }

  private scrollActivePaletteButtonIntoView(): void {
    setTimeout(() => {
      const activeButton = document.querySelector<HTMLElement>('.question-palette-button-active');

      activeButton?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'smooth',
      });
    }, 0);
  }

  private markQuestionAsVisited(questionId: number): void {
    this.visitedQuestions.update((visited) => ({
      ...visited,
      [questionId]: true,
    }));
  }

  private markCurrentQuestionAsVisited(): void {
    const currentQuestion = this.activePartQuestions()[this.activeQuestionIndex()];

    if (!currentQuestion) {
      return;
    }

    this.trackQuestionEntry(currentQuestion);
  }

  // QUESTION_VIEWED / QUESTION_REVISITED / QUESTION_SKIPPED are derived
  // server-side from these ENTERED/EXITED pairs.
  private trackQuestionEntry(question: QuestionPaperItem): void {
    this.eventTracker.setActiveQuestionId(question.sourceQuestionId ?? null);
    this.eventTracker.track(
      'QUESTION_ENTERED',
      {},
      { questionId: question.sourceQuestionId ?? null },
    );
    this.markQuestionAsVisited(question.id);
  }

  private trackQuestionExit(question: QuestionPaperItem): void {
    this.eventTracker.track(
      'QUESTION_EXITED',
      {},
      { questionId: question.sourceQuestionId ?? null },
    );
    this.eventTracker.setActiveQuestionId(null);
  }

  // Parts carry no subject of their own, so subjectId is reported as null.
  private trackSectionEntry(): void {
    const part = this.activePart();

    if (part) {
      this.eventTracker.track('SECTION_ENTERED', { sectionId: part.id, subjectId: null });
    }
  }

  private trackSectionExit(): void {
    const part = this.activePart();

    if (part) {
      this.eventTracker.track('SECTION_EXITED', { sectionId: part.id, subjectId: null });
    }
  }

  private formatTime(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  private startPartTimer(): void {
    const activePart = this.activePart();
    const activePartIndex = this.activePartIndex();

    // Calculate how much time this part should actually get. If the user's tab was backgrounded
    // and they "spilled over" into the next part's time, we deduct that spilled time.
    const futurePartsDuration = this.parts()
      .slice(activePartIndex + 1)
      .reduce((sum, part) => sum + this.getPartDurationInSeconds(part), 0);

    const nominalDuration = this.getPartDurationInSeconds(activePart);
    const partDurationInSeconds = Math.min(
      nominalDuration,
      Math.max(this.totalTimeLeftInSeconds() - futurePartsDuration, 0),
    );

    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }

    this.partTimeLeftInSeconds.set(partDurationInSeconds);
    this.hideTimeWarning();

    if (partDurationInSeconds === 0) {
      this.handlePartTimeExpired();
      return;
    }

    const targetPartEndTime = Date.now() + partDurationInSeconds * 1000;
    const targetTotalEndTime = Date.now() + this.totalTimeLeftInSeconds() * 1000;
    let warningShown = false;

    this.timerId = setInterval(() => {
      const nextPartTime = Math.max(Math.round((targetPartEndTime - Date.now()) / 1000), 0);
      const nextTotalTime = Math.max(Math.round((targetTotalEndTime - Date.now()) / 1000), 0);

      this.partTimeLeftInSeconds.set(nextPartTime);
      this.totalTimeLeftInSeconds.set(nextTotalTime);

      if (nextPartTime === 0 || nextTotalTime === 0) {
        if (this.timerId) {
          clearInterval(this.timerId);
          this.timerId = null;
        }

        this.handlePartTimeExpired();
        return;
      }

      if (nextPartTime <= 300 && !warningShown) {
        warningShown = true;
        this.openTimeWarning(nextPartTime);
      }
    }, 1000);
  }

  private startResumedPartTimer(): void {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }

    this.hideTimeWarning();

    const currentPartTime = this.partTimeLeftInSeconds();

    if (currentPartTime === 0) {
      this.handlePartTimeExpired();
      return;
    }

    const targetPartEndTime = Date.now() + currentPartTime * 1000;
    const targetTotalEndTime = Date.now() + this.totalTimeLeftInSeconds() * 1000;
    let warningShown = false;

    if (currentPartTime <= 300) {
      warningShown = true;
      this.openTimeWarning(currentPartTime);
    }

    this.timerId = setInterval(() => {
      const nextPartTime = Math.max(Math.round((targetPartEndTime - Date.now()) / 1000), 0);
      const nextTotalTime = Math.max(Math.round((targetTotalEndTime - Date.now()) / 1000), 0);

      this.partTimeLeftInSeconds.set(nextPartTime);
      this.totalTimeLeftInSeconds.set(nextTotalTime);

      if (nextPartTime === 0 || nextTotalTime === 0) {
        if (this.timerId) {
          clearInterval(this.timerId);
          this.timerId = null;
        }

        this.handlePartTimeExpired();
        return;
      }

      if (nextPartTime <= 300 && !warningShown) {
        warningShown = true;
        this.openTimeWarning(nextPartTime);
      }
    }, 1000);
  }

  private handlePartTimeExpired(): void {
    if (this.activePartIndex() < this.parts().length - 1) {
      this.goToNextPart();
      return;
    }

    this.popupMode.set('time-expired');

    if (this.submitTimerId) {
      clearTimeout(this.submitTimerId);
    }

    this.submitTimerId = setTimeout(() => {
      this.submitExam(false, 'time-expired');
    }, 2000);
  }

  // Not a proctoring check — no counting, no warning, no auto-submit. Shows
  // the "resume / exit" prompt (with its close cross) so there's always a
  // visible, deliberate way back to fullscreen or out of the exam, however
  // many times this fires — and silently keeps saved progress current in
  // case the student doesn't come back.
  @HostListener('document:fullscreenchange')
  protected onFullscreenChange(): void {
    if (this.hadFullscreen && !document.fullscreenElement) {
      this.hadFullscreen = false;
      this.eventTracker.track('FULLSCREEN_EXITED');
      this.syncProgressSilently();
      this.toastr.warning(
        'You have exited full screen mode. Please return to full screen to continue the exam.',
        'Full screen exited',
      );
      this.popupMode.set('fullscreen-exit');
    }
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    if (this.resizeDebounceTimerId) {
      clearTimeout(this.resizeDebounceTimerId);
    }

    this.resizeDebounceTimerId = setTimeout(() => {
      this.eventTracker.track('SCREEN_RESIZED', {
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }, 400);
  }

  @HostListener('window:offline')
  protected onNetworkOffline(): void {
    this.eventTracker.track('NETWORK_DISCONNECTED');
  }

  @HostListener('window:online')
  protected onNetworkOnline(): void {
    this.eventTracker.track('NETWORK_RECONNECTED');
  }

  // The exam page being hidden — switching browser tab, minimizing, dragging
  // the tab out, or on touch devices the browser app going to the background
  // (hardware back button, home, app switch) — exits exactly like the "Exit
  // Examination" button on every device: progress is saved as IN_PROGRESS and
  // can be resumed from the dashboard. The exam must never keep running in a
  // background tab. It is always reported as BROWSER_RESTRICTION_TRIGGERED
  // {reason: tab_switch}; proctoring only decides whether it is also toasted.
  // Plain window blur alone (OS focus blips, fullscreen/permission prompts,
  // devtools) is not treated as a tab switch — it only silently keeps saved
  // progress current.
  @HostListener('document:visibilitychange')
  protected onVisibilityChange(): void {
    this.eventTracker.track(
      document.visibilityState === 'hidden' ? 'BROWSER_TAB_HIDDEN' : 'BROWSER_TAB_VISIBLE',
    );
    this.handleFocusChange();

    if (document.visibilityState === 'hidden') {
      this.handleTabHidden();
    }
  }

  private handleTabHidden(): void {
    if (this.hasEndedEventSession || this.hasHandledSecurityTermination || !this.preparedState()) {
      return;
    }

    this.securityService.reportViolation(SecurityViolationType.TAB_SWITCH);
    this.exitExamOnTabSwitch();
  }

  /**
   * Called by examLeaveGuard for every navigation off this screen. The
   * screen's own exits (submit, exit, tab switch, load failures) have already
   * ended the attempt and pass straight through. Anything else — browser
   * back/forward, header or sidebar links — is cancelled and replaced by the
   * matching exit: a finished attempt (time up / server auto-submit) is
   * submitted, otherwise progress is saved as IN_PROGRESS so it can be
   * resumed from the dashboard until the timer runs out.
   */
  canLeaveExam(): boolean {
    if (
      this.isLeavingExam ||
      this.hasEndedEventSession ||
      !this.preparedState() ||
      !this.parts().length
    ) {
      return true;
    }

    // Browser back/forward is already reported by the environment monitor.
    const navigation = this.router.getCurrentNavigation();

    // The browser's back/forward buttons can't be disabled by a page, so the
    // navigation is cancelled instead (the router's 'computed' resolution
    // restores the history position) and the student stays on the exam.
    if (
      navigation?.trigger === 'popstate' &&
      !this.isViolationSubmitOpen() &&
      this.popupMode() !== 'time-expired' &&
      this.totalTimeLeftInSeconds() > 0
    ) {
      this.toastr.warning(
        'Browser back and forward buttons are disabled during the examination.',
        'Navigation disabled',
      );
      return false;
    }

    this.isLeavingExam = true;

    if (navigation?.trigger !== 'popstate') {
      this.eventTracker.track('UNAUTHORIZED_NAVIGATION_ATTEMPTED', {
        targetUrl: navigation
          ? this.router.serializeUrl(navigation.finalUrl ?? navigation.extractedUrl)
          : null,
        trigger: 'in_app_link',
      });
    }

    if (this.isViolationSubmitOpen()) {
      this.isViolationSubmitOpen.set(false);
      this.submitExam(true, 'server');
      return false;
    }

    if (this.popupMode() === 'time-expired' || this.totalTimeLeftInSeconds() <= 0) {
      if (this.submitTimerId) {
        clearTimeout(this.submitTimerId);
        this.submitTimerId = null;
      }

      this.submitExam(true, 'time-expired');
      return false;
    }

    this.toastr.warning(
      'You navigated away from the exam, so you have been exited from the examination. You can resume it from the dashboard until the timer ends.',
      'Exited examination',
    );
    this.exitToTestSeries();
    return false;
  }

  private exitExamOnTabSwitch(): void {
    if (this.hasExitedOnTabSwitch || this.hasHandledSecurityTermination || !this.preparedState()) {
      return;
    }

    this.hasExitedOnTabSwitch = true;
    this.toastr.warning(
      isTouchDevice()
        ? 'The exam app went to the background, so you have been exited from the examination. Your progress is saved — you can resume it from the dashboard until the timer ends.'
        : 'You switched away from the exam tab, so you have been exited from the examination. You can resume it from the dashboard.',
      'Exited examination',
    );
    this.exitToTestSeries();
  }

  private handleFocusChange(): void {
    const lostFocus = !this.securityService.hasExamFocus();

    if (lostFocus && !this.hasReportedFocusLoss) {
      this.hasReportedFocusLoss = true;
      this.syncProgressSilently();
    } else if (!lostFocus) {
      this.hasReportedFocusLoss = false;
    }
  }

  // Fires only once the browser is actually tearing the page down (tab/browser close,
  // hard refresh, OS shutdown) — not on in-app router navigation.
  @HostListener('window:pagehide')
  protected onPageHide(): void {
    // The student closed the exam (tab/browser close, or a reload confirmed
    // past the leave warning) while it was still running — a deliberate
    // close, so APP_FORCE_CLOSED rather than APP_TERMINATED_UNEXPECTEDLY.
    if (!this.hasEndedEventSession && this.preparedState()) {
      this.eventTracker.track('APP_FORCE_CLOSED', { trigger: 'page_unload' }, { questionId: this.eventTracker.activeQuestionId });
    }

    this.saveReloadSnapshot();
    this.submitExamInBackground();
    // ngOnDestroy never runs on a real unload, so close the event session
    // here while the socket can still carry it. A reload/close that gets past
    // the browser's warning saves the attempt exactly like "Exit Examination"
    // (TEST_EXITED), so it resumes from the same point afterwards.
    this.endEventTrackingSession('tab_closed', 'TEST_EXITED');
  }

  private saveReloadSnapshot(): void {
    const examId = this.preparedState()?.examId;

    // Nothing to restore once the attempt has been submitted or exited.
    if (!examId || this.hasEndedEventSession) {
      return;
    }

    const snapshot: ReloadSnapshot = {
      examId,
      attemptId: this.examSessionService.activeExamMeta()?.attemptId,
      savedAt: Date.now(),
      savedAnswers: this.savedAnswers(),
      selectedAnswers: this.selectedAnswers(),
      visitedQuestions: this.visitedQuestions(),
      markedForReview: this.markedForReview(),
      guessedQuestions: this.guessedQuestions(),
      resumePartIndex: this.activePartIndex(),
      resumeQuestionIndex: this.activeQuestionIndex(),
      totalTimeLeftInSeconds: this.totalTimeLeftInSeconds(),
      partTimeLeftInSeconds: this.partTimeLeftInSeconds(),
    };

    try {
      sessionStorage.setItem(RELOAD_SNAPSHOT_KEY, JSON.stringify(snapshot));
    } catch {
      // Storage unavailable — the reload falls back to the server's resume data.
    }
  }

  /**
   * Returns (and always discards) the snapshot saved by the previous page,
   * but only when this load is a reload of the same attempt. Timers are
   * reduced by the time the reload itself took.
   */
  private takeReloadSnapshot(): ResumeData | null {
    let snapshot: ReloadSnapshot | null = null;

    try {
      const raw = sessionStorage.getItem(RELOAD_SNAPSHOT_KEY);
      sessionStorage.removeItem(RELOAD_SNAPSHOT_KEY);
      snapshot = raw ? (JSON.parse(raw) as ReloadSnapshot) : null;
    } catch {
      return null;
    }

    const navigation = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;

    if (
      !snapshot ||
      navigation?.type !== 'reload' ||
      snapshot.examId !== this.preparedState()?.examId ||
      snapshot.attemptId !== this.examSessionService.activeExamMeta()?.attemptId
    ) {
      return null;
    }

    const elapsedSeconds = Math.max(Math.floor((Date.now() - snapshot.savedAt) / 1000), 0);

    return {
      ...snapshot,
      totalTimeLeftInSeconds: Math.max(snapshot.totalTimeLeftInSeconds - elapsedSeconds, 0),
      partTimeLeftInSeconds: Math.max(snapshot.partTimeLeftInSeconds - elapsedSeconds, 0),
    };
  }

  private submitExamInBackground(): void {
    if (this.hasSubmittedOnUnload) {
      return;
    }

    this.hasSubmittedOnUnload = true;
    this.postProgressSnapshot();
  }

  /**
   * Best-effort save of current progress that does not navigate away, lock,
   * or otherwise interrupt the exam — just keeps the backend's copy of
   * answers current so they're up to date if the student comes back.
   */
  private syncProgressSilently(): void {
    if (this.hasSubmittedOnUnload) {
      return;
    }

    this.postProgressSnapshot();
  }

  /**
   * Uses the normal HttpClient call (same one the final submission uses),
   * not a keepalive fetch — so a save that's still in flight when the tab
   * actually closes can get cancelled by the browser. That's an accepted
   * tradeoff: at most the last save or two is lost, not silently unreliable
   * requests bypassing the app's usual interceptors/error handling.
   */
  private postProgressSnapshot(): void {
    // Guards against the three independent triggers (question-count, tab/fullscreen
    // distraction, unload) firing overlapping requests at the same moment.
    if (this.isPostingProgressSnapshot) {
      return;
    }

    this.isPostingProgressSnapshot = true;

    const payload = this.buildSubmitPayload('IN_PROGRESS');

    // Submit API is no longer needed mid-exam — every answer change already
    // reaches the server over the event WebSocket. Kept for reference.
    // Mid-exam saves report ANSWER_SAVE_* once per answer the snapshot
    // carries for the first time (the server compares them against the
    // reconstructed answer trace); the TEST_SYNC_* events are sent once, when
    // the attempt ends (endEventTrackingSession).
    // this.testSeriesService.submitExam(payload).subscribe({
    //   next: () => {
    //     this.isPostingProgressSnapshot = false;
    //     savingQuestionIds.forEach((questionId) => {
    //       this.unsavedAnswerQuestionIds.delete(questionId);
    //       this.trackAnswerSave('ANSWER_SAVE_SUCCESS', questionId);
    //     });
    //   },
    //   error: () => {
    //     // Best-effort save; the answers stay pending and ride along with the next snapshot.
    //     this.isPostingProgressSnapshot = false;
    //     savingQuestionIds.forEach((questionId) =>
    //       this.trackAnswerSave('ANSWER_SAVE_FAILED', questionId),
    //     );
    //   },
    // });

    // No ANSWER_SAVE_SUCCESS here — each saved answer is reported as
    // ANSWER_CONFIRMED when the student clicks Save & Next / Mark for Review & Next.
    this.isPostingProgressSnapshot = false;
  }

  private buildSubmissionSummary(): ExamSubmissionSummary {
    const remainingTimeInSeconds = Math.max(Math.floor(this.totalTimeLeftInSeconds()), 0);
    const timeTakenInSeconds = Math.max(
      Math.floor(this.totalDurationInSeconds()) - remainingTimeInSeconds,
      0,
    );
    // Full h/m/s breakdown so neither value silently drops seconds (e.g. "3m 8s", "1h 0m 5s").
    const formatDuration = (totalSeconds: number): string => {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`;
      }
      return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    };
    const timeTaken = formatDuration(timeTakenInSeconds);
    const remainingTime = formatDuration(remainingTimeInSeconds);

    return {
      totalQuestions: this.totalQuestionCount(),
      totalAttempted: this.totalAttemptedCount(),
      totalUnattempted: this.totalQuestionCount() - this.totalAttemptedCount(),
      markedForReview: this.totalMarkedForReviewCount(),
      timeTaken,
      remainingTime,
    };
  }

  private buildExamResultState(status: 'IN_PROGRESS' | 'COMPLETED'): ExamResultState {
    const submissionSummary = this.buildSubmissionSummary();
    const preparedState = this.preparedState();

    // A server-confirmed auto-submit (duration expiry, violation limit) already has the real
    // values by this point — see lastServerSubmissionResult's doc comment. Everything else
    // (manual submit, exiting to save progress) still estimates locally.
    const serverResult = status === 'COMPLETED' ? this.lastServerSubmissionResult : null;
    const submittedAt = (serverResult?.submittedAt && Date.parse(serverResult.submittedAt)) || Date.now();
    const reviewUnlockAt = serverResult
      ? (serverResult.reviewAvailableAt ? Date.parse(serverResult.reviewAvailableAt) : null)
      : getReviewUnlockTimestamp(preparedState?.resultConfig, {
          submittedAt,
          testEndAt: submittedAt + Math.max(this.totalTimeLeftInSeconds(), 0) * 1000,
          expiryTimestamp: preparedState?.expiryTimestamp ?? null,
          timeZone: preparedState?.timeZone,
        });

    return {
      status,
      examTitle: this.popupHeading(),
      examMeta: preparedState?.examMeta ?? '-',
      submittedAt: new Date(submittedAt).toISOString(),
      expiryTimestamp: preparedState?.expiryTimestamp ?? null,
      reviewUnlockAt,
      reviewLockedMessage: getReviewLockedMessage(preparedState?.resultConfig, reviewUnlockAt),
      candidateName: this.getCandidateDisplayName(),
      candidateInitials: this.getCandidateInitials(),
      performanceLabel: submissionSummary.totalQuestions
        ? submissionSummary.totalAttempted / submissionSummary.totalQuestions >= 0.9
          ? 'Excellent Finish'
          : submissionSummary.totalAttempted / submissionSummary.totalQuestions >= 0.7
            ? 'Great Performance!'
            : submissionSummary.totalAttempted / submissionSummary.totalQuestions >= 0.5
              ? 'Good Progress'
              : 'Keep Practicing'
        : 'Keep Practicing',
      submissionSummary,
      sectionSummaries: this.parts().map((part, index) => this.buildSectionSummary(part, index)),
    };
  }

  private buildSubmitPayload(status: 'IN_PROGRESS' | 'COMPLETED'): SubmitExamPayload {
    const sections = this.parts().map((part) => this.buildSubmitSection(part));
    const activeScreenTimeInSeconds = Math.max(
      Math.floor((Date.now() - this.screenEntryTimestamp) / 1000),
      0,
    );

    return {
      sections,
      exam_id: this.preparedState()?.examId ?? '',
      total_questions: this.totalQuestionCount(),
      total_attempted: this.totalAttemptedCount(),
      total_unattempted: this.totalQuestionCount() - this.totalAttemptedCount(),
      total_time_taken: activeScreenTimeInSeconds,
      status,
    };
  }

  private buildSubmitSection(part: QuestionPart): SubmitExamSectionPayload {
    const answers = this.questions()
      .filter((question) => question.part === part.label)
      .map((question) => {
        const selectedOptionId = this.savedAnswers()[question.id];
        const selectedOption = question.options.find((option) => option.id === selectedOptionId);

        return {
          question_number: question.id,
          question_id: question.sourceQuestionId ?? '',
          selected_answer: selectedOption
            ? {
                key: selectedOption.label,
                text: selectedOption.text,
              }
            : null,
          is_skipped: !selectedOption && this.isVisited(question.id),
          is_guessing: this.isGuessing(question.id),
          marked_for_review: this.isMarked(question.id),
          is_not_visited: !this.isVisited(question.id),
          is_question_answered: Boolean(selectedOption),
        };
      });

    return {
      section_name: part.label,
      answers,
    };
  }

  private buildSectionSummary(part: QuestionPart, index: number): ExamSectionSummary {
    const partQuestions = this.questions().filter((question) => question.part === part.label);
    const totalQuestions = partQuestions.length;
    const answered = partQuestions.filter((question) => this.hasSavedAnswer(question.id)).length;
    const markedForReview = partQuestions.filter((question) => this.isMarked(question.id)).length;
    const guessedCount = partQuestions.filter((question) => this.isGuessing(question.id)).length;
    const unanswered = totalQuestions - answered;
    const accuracy = totalQuestions ? Math.round((answered / totalQuestions) * 1000) / 10 : 0;

    return {
      id: part.id,
      label: `Section ${String.fromCharCode(65 + index)}`,
      badge: String.fromCharCode(65 + index),
      badgeTone:
        (['indigo', 'green', 'orange', 'violet', 'teal'][
          index
        ] as ExamSectionSummary['badgeTone']) ?? 'indigo',
      totalQuestions,
      answered,
      unanswered,
      markedForReview,
      marksObtained: `${answered}/${totalQuestions}`,
      accuracy,
      guessedAnswers: `${guessedCount}/${totalQuestions}`,
    };
  }

  private getPartDurationInSeconds(part: QuestionPart | null | undefined): number {
    if (this.shouldUseFixedSectionTimer()) {
      return FIXED_SECTION_DURATION_IN_SECONDS;
    }

    return Math.max(Math.round((part?.durationInMinutes ?? 0) * 60), 0);
  }

  private getStoredUserId(): string {
    const directUserId = this.storageService.getItem<string>('userId');

    if (typeof directUserId === 'string' && directUserId.trim()) {
      return directUserId.trim();
    }

    const storedUser = this.storageService.getItem<{
      user_id?: string;
      userId?: string;
      id?: string;
    }>('userData');
    return (
      storedUser?.user_id?.trim() || storedUser?.userId?.trim() || storedUser?.id?.trim() || ''
    );
  }

  private getCandidateDisplayName(): string {
    const name = this.userProfile()?.name?.trim() ?? '';
    return name || 'User';
  }

  private getCandidateInitials(): string {
    const nameParts = this.getCandidateDisplayName().split(/\s+/).filter(Boolean);

    if (!nameParts.length) {
      return 'U';
    }

    return `${nameParts[0]?.charAt(0) ?? ''}`.toUpperCase();
  }

  protected getPartOverview(partLabel: string): (QuestionPart & { order: number }) | null {
    const index = this.parts().findIndex((part) => part.label === partLabel);
    return index === -1 ? null : { ...this.parts()[index], order: index + 1 };
  }

  private getFormattedPartLabels(): string {
    const labels = this.parts().map((part) => part.label);

    if (!labels.length) {
      return 'Group A';
    }

    if (labels.length === 1) {
      return labels[0];
    }

    if (labels.length === 2) {
      return `${labels[0]} & ${labels[1]}`;
    }

    return `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
  }

  private getPartQuestionText(): string {
    const questionCounts = this.parts().map((part) => Number.parseInt(part.info, 10) || 0);
    const uniqueQuestionCounts = Array.from(new Set(questionCounts));

    if (uniqueQuestionCounts.length <= 1) {
      return `${questionCounts[0] ?? 0} questions`;
    }

    const minimum = Math.min(...uniqueQuestionCounts);
    const maximum = Math.max(...uniqueQuestionCounts);
    return `${minimum}-${maximum} questions`;
  }

  private getPartDurationText(): string {
    const durations = this.parts().map((part) => part.durationInMinutes);
    const uniqueDurations = Array.from(new Set(durations));

    if (uniqueDurations.length <= 1) {
      return `${durations[0] ?? 0} minutes`;
    }

    const minimum = Math.min(...uniqueDurations);
    const maximum = Math.max(...uniqueDurations);
    return `${minimum}-${maximum} minutes`;
  }

  private commitCurrentAnswer(questionId: number, trackConfirmation = true): void {
    const selectedOptionId = this.selectedAnswers()[questionId];
    const previouslySavedOptionId = this.savedAnswers()[questionId];

    this.savedAnswers.update((answers) => {
      const nextAnswers = { ...answers };

      if (selectedOptionId) {
        nextAnswers[questionId] = selectedOptionId;
      } else {
        delete nextAnswers[questionId];
      }

      return nextAnswers;
    });

    // Sent on Save & Next / Mark for Review & Next when an option is selected, or
    // with a null optionId when a previously saved answer was cleared. Nothing is
    // sent when there's no selection and nothing was saved before.
    if (trackConfirmation && (selectedOptionId || previouslySavedOptionId)) {
      const question = this.questions().find((candidate) => candidate.id === questionId);
      this.eventTracker.track(
        'ANSWER_CONFIRMED',
        {
          optionId: selectedOptionId ? this.getOptionLabel(question, selectedOptionId) : null,
        },
        {
          questionId: question?.sourceQuestionId ?? null,
        },
      );
      // this.eventTracker.track('ANSWER_CONFIRMED', {}, { questionId: question?.sourceQuestionId ?? null ,optionId});
    }
  }

  /**
   * Background heartbeat save: rather than hitting the backend on every single
   * question, we batch it — the in-memory selectedAnswers/savedAnswers state
   * (already updated live as the student answers) gets POSTed as an
   * IN_PROGRESS snapshot once every PROGRESS_SYNC_QUESTION_INTERVAL questions
   * committed, independent of the distraction-triggered syncs.
   */
  private syncProgressEveryFewQuestions(): void {
    this.questionsCommittedSinceLastSync += 1;

    if (this.questionsCommittedSinceLastSync < PROGRESS_SYNC_QUESTION_INTERVAL) {
      return;
    }

    this.questionsCommittedSinceLastSync = 0;
    this.postProgressSnapshot();
  }
}
